import React, { useEffect } from 'react'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { CircularProgress } from '@mui/material'
import { Box } from '@mui/system'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { Add } from '@mui/icons-material'
import CustomInput from '../../../../../components/muiComponents/customInput'
import { testApiSlice } from '../../../../../features/section/testSlice'
import { requiredString } from '../../../../../utils/validation'
import { TestState } from '../../../../../features/types'

const styles = {
    flexSpace: {
        display: 'flex',
        justifyContent: 'space-between',
    },
}

interface Props {
    courseTopicId: number
    confirmAction?: (id?: string) => void
    editTest?: any
    editMode?: boolean
    getSections?: () => void
    setEditRow?: (element: any) => void
}

export default function AddTestDialog({
    editTest,
    courseTopicId,
    getSections,
    setEditRow,
}: Props) {
    const [open, setOpen] = React.useState(false)
    const { formatMessage } = useIntl()

    const [createTest, { isLoading: isCreating }] =
        testApiSlice.useCreateTestMutation()
    const [updateTest, { isLoading: isUpdating }] =
        testApiSlice.useUpdateTestMutation()

    const test: TestState = {
        id: null,
        title: '',
        description: '',
        duration: 0,
        isReady: true,
        position: 0,
        courseTopicId: courseTopicId,
    }

    const validationSchema = yup.object().shape({
        title: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: test,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editTest) {
                handleUpdateTest()
            } else {
                handleCreateTest()
            }
        },
    })

    const handleCreateTest = async () => {
        try {
            await createTest(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            getSections()
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleUpdateTest = async () => {
        try {
            await updateTest(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            getSections()
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleClickOpen = () => {
        formik.resetForm()
        setOpen(true)
    }

    const handleClose = () => {
        setEditRow('')
        setOpen(false)
    }

    useEffect(() => {
        if (editTest) {
            setOpen(true)
            formik.setValues(editTest)
        }
    }, [editTest])

    return (
        <div>
            <Button
                endIcon={<Add />}
                onClick={handleClickOpen}
            >
                {formatMessage({ id: 'add' })} {formatMessage({ id: 'test' })}
            </Button>

            <Dialog
                maxWidth={'sm'}
                fullWidth
                open={open}
                onClose={handleClose}
            >
                <DialogTitle>
                    {editTest
                        ? formatMessage({ id: 'edit' })
                        : formatMessage({ id: 'add' })}{' '}
                    {formatMessage({ id: 'test' }).toLowerCase()}
                </DialogTitle>

                <DialogContent>
                    <form>
                        <DialogContentText>
                            {formatMessage({ id: 'name' })}
                        </DialogContentText>

                        <CustomInput
                            id="title"
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            helperText={formik.errors.title}
                            error={Boolean(formik.errors.title)}
                            fullWidth
                        />

                        <DialogContentText>
                            {formatMessage({ id: 'description' })}
                        </DialogContentText>

                        <CustomInput
                            id="description"
                            name="description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            fullWidth
                            multiline
                            maxRows={10}
                            minRows={10}
                        />

                        <Box sx={styles.flexSpace}>
                            <Box>
                                <DialogContentText>
                                    {formatMessage({ id: 'duration' })}
                                </DialogContentText>

                                <CustomInput
                                    id="duration"
                                    name="duration"
                                    value={formik.values.duration}
                                    onChange={formik.handleChange}
                                    fullWidth
                                />
                            </Box>

                            <Box>
                                <DialogContentText>
                                    {formatMessage({ id: 'position' })}
                                </DialogContentText>

                                <CustomInput
                                    id="position"
                                    name="position"
                                    value={formik.values.position}
                                    onChange={formik.handleChange}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        {/* <FormControlLabel
                            control={
                                <Checkbox
                                    name="isReady"
                                    checked={formik?.values?.isReady}
                                    onChange={formik.handleChange}
                                />
                            }
                            label={formatMessage({ id: 'ready' }).concat(' ?')}
                        /> */}
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => formik.handleSubmit()}
                        disabled={isCreating || isUpdating}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                sx={{ mr: 1 }}
                            />
                        ) : editTest ? (
                            formatMessage({ id: 'edit' })
                        ) : (
                            formatMessage({ id: 'add' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
